import {
  Button,
  Card,
  Collapse,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { PatientMeasurementFieldTile } from './PatientMeasurementFieldTile';
import { useMemo, useState } from 'react';
import type { PatientBaseProfile, PatientMeasurementRecord } from '../types';
import { usePatientMeasurementsHistoryQuery } from '../hooks/usePatientsQueries';
import {
  buildNutritionInput,
  calculatePatientNutritionMetrics,
} from '../utils/nutrition';
import { DeletePatientMeasurementDialog } from './DeletePatientMeasurementDialog';
import { PatientMeasurementViewModal } from './PatientMeasurementViewModal';
import { NutritionMetricsTable } from './NutritionMetricsTable';
import { PatientMeasurementsForm } from './PatientMeasurementsForm';
import { PatientMeasurementsHistoryChart } from './PatientMeasurementsHistoryChart';
import { PatientMeasurementsHistoryTable } from './PatientMeasurementsHistoryTable';
import { formatDateTimeValue } from '../utils/formatters';

interface PatientBodyDomainTabProps {
  patientId: number;
  profile: PatientBaseProfile;
  isFormOpen: boolean;
  isSaving: boolean;
  isDeletingMeasurement?: boolean;
  deletingMeasurementId?: number | null;
  initialValues: PatientMeasurementRecord;
  onOpenForm: () => void;
  onCloseForm: () => void;
  onSubmit: (values: PatientMeasurementRecord) => Promise<void> | void;
  onDeleteMeasurement?: (measurementId: number) => Promise<void> | void;
}

export function PatientBodyDomainTab({
  patientId,
  profile,
  isFormOpen,
  isSaving,
  isDeletingMeasurement = false,
  deletingMeasurementId = null,
  initialValues,
  onOpenForm,
  onCloseForm,
  onSubmit,
  onDeleteMeasurement,
}: PatientBodyDomainTabProps) {
  const measurementsQuery = usePatientMeasurementsHistoryQuery(patientId);
  const history = measurementsQuery.data ?? [];
  const latestMeasurement = history[0] ?? null;
  const metrics = useMemo(() => {
    const nutritionInput = buildNutritionInput(profile, latestMeasurement);
    return calculatePatientNutritionMetrics(nutritionInput);
  }, [latestMeasurement, profile]);

  const [measurementToDelete, setMeasurementToDelete] =
    useState<PatientMeasurementRecord | null>(null);
  const [measurementToView, setMeasurementToView] =
    useState<PatientMeasurementRecord | null>(null);

  const handleConfirmDelete = async () => {
    if (measurementToDelete?.id == null || onDeleteMeasurement == null) {
      return;
    }

    const deletedId = measurementToDelete.id;

    try {
      await onDeleteMeasurement(deletedId);
      setMeasurementToDelete(null);
      if (measurementToView?.id === deletedId) {
        setMeasurementToView(null);
      }
    } catch {
      // Error toast handled by mutation.
    }
  };

  return (
    <Stack gap='lg'>
      <Group justify='space-between' align='center' wrap='wrap'>
        <Stack gap={2}>
          <Text fw={600}>Rilevazioni</Text>
          <Text size='xs' c='dimmed'>
            Andamento, ultima misura e storico corporeo dell&apos;utente.
          </Text>
        </Stack>
        {!isFormOpen && <Button onClick={onOpenForm}>Nuova rilevazione</Button>}
      </Group>

      <Collapse in={isFormOpen}>
        <Card withBorder p='md' radius='sm'>
          <PatientMeasurementsForm
            patientId={patientId}
            initialValues={initialValues}
            baseProfile={profile}
            saving={isSaving}
            onCancel={onCloseForm}
            onSubmit={onSubmit}
          />
        </Card>
      </Collapse>

      <PatientMeasurementsHistoryChart history={history} />

      <Paper withBorder p='md' radius='sm'>
        <Stack gap='sm'>
          <Group
            justify='space-between'
            align='flex-start'
            wrap='wrap'
            gap='sm'
          >
            <Stack gap={2}>
              <Text fw={600}>Ultima rilevazione</Text>
              {latestMeasurement != null ? (
                <Text size='sm' c='dimmed'>
                  {formatDateTimeValue(
                    latestMeasurement.createdAt ??
                      latestMeasurement.updatedAt
                  )}
                </Text>
              ) : null}
            </Stack>
          </Group>

          {latestMeasurement == null ? (
            <Text size='sm' c='dimmed'>
              Nessuna rilevazione disponibile. Registra la prima per attivare
              metriche e storico.
            </Text>
          ) : (
            <Stack gap='md'>
              {latestMeasurement.fields.length === 0 ? (
                <Text size='sm' c='dimmed'>
                  Nessun campo registrato in questa rilevazione.
                </Text>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='sm'>
                  {latestMeasurement.fields.map(field => (
                    <PatientMeasurementFieldTile
                      key={String(
                        field.id ?? `${field.label}-${field.metricType}`
                      )}
                      field={field}
                    />
                  ))}
                </SimpleGrid>
              )}

              <Paper withBorder p='md' radius='sm'>
                <Stack gap='xs'>
                  <Text fw={600}>Note rilevazione corrente</Text>
                  <Text size='sm' c='dimmed'>
                    {latestMeasurement.notes.trim() || 'Nessuna nota'}
                  </Text>
                </Stack>
              </Paper>
            </Stack>
          )}
        </Stack>
      </Paper>

      <PatientMeasurementsHistoryTable
        history={history}
        deletingMeasurementId={deletingMeasurementId}
        onView={measurement => setMeasurementToView(measurement)}
        onDelete={
          onDeleteMeasurement
            ? measurement => setMeasurementToDelete(measurement)
            : undefined
        }
      />

      <NutritionMetricsTable metrics={metrics} />

      <PatientMeasurementViewModal
        measurement={measurementToView}
        opened={measurementToView != null}
        onClose={() => setMeasurementToView(null)}
      />

      <DeletePatientMeasurementDialog
        measurement={measurementToDelete}
        opened={measurementToDelete != null}
        loading={isDeletingMeasurement}
        onClose={() => setMeasurementToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </Stack>
  );
}
